{
  "title": "API and integrations",
  "intro": "Keys let scripts, CI and AI agents manage your infrastructure through our API. Each key may do only what its policy allows, in its projects, and never more than its owner.",
  "tabs": {
    "keys": "Keys",
    "service-accounts": "Service accounts",
    "policies": "Policies",
    "endpoints": "Endpoints",
    "activity": "Activity",
    "approvals": "Approvals",
    "apps": "Connected apps"
  },
  "intros": {
    "policies": "A policy says what a key may do: which actions, in which projects, and which of them wait for a person's approval.",
    "approvals": "Requests of keys and AI agents that wait for a person, and what became of the earlier ones.",
    "apps": "AI clients you connected to the panel by signing in (claude.ai, ChatGPT and others): what each may do, and how to disconnect it."
  },
  "messengers": {
    "title": "Messengers",
    "intro": "The panel sends alerts and approval requests through messengers. An organisation administrator connects them; then everyone links their own account in Notification settings.",
    "kindsLabel": "Messengers",
    "notSet": "Not set up",
    "adminsOnly": "Organisation administrators connect messengers. You link your own Telegram for alerts in Notification settings.",
    "telegram": {
      "name": "Telegram",
      "about": "The organisation's bots: alerts, approval requests, projects' group chats.",
      "bots": "Bots: {{n}}"
    }
  },
  "status": {
    "active": "Active",
    "expired": "Expired",
    "revoked": "Revoked"
  },
  "policy": {
    "custom": "Own rules",
    "customLead": "Pick what the key may do in a matrix of resources and actions.",
    "library": "Policy of your organisation"
  },
  "projects": {
    "all": "All projects"
  },
  "yes": "Yes",
  "no": "No",
  "copy": "Copy",
  "copyThing": "Copy: {{thing}}",
  "copied": "Copied.",
  "copyFailed": "Could not copy. Select the text and copy it yourself.",
  "snippets": {
    "env": "Environment variable",
    "curl": "Try it with curl",
    "curlHint": "Lists the projects the key reaches."
  },
  "keys": {
    "create": "Create key",
    "empty": "No API keys yet",
    "emptyLead": "Create a key for a script, a CI pipeline or an AI agent. You choose what it may do and where; it never gets more rights than you have.",
    "name": "Key",
    "owner": "Owner",
    "serviceAccount": "Service account",
    "policy": "Policy",
    "projects": "Projects",
    "expires": "Expires",
    "never": "Never",
    "lastUsed": "Last used",
    "notUsed": "Not used yet",
    "status": "Status",
    "open": "Open"
  },
  "revoke": {
    "title": "Revoke the key «{{name}}»?",
    "stops": "Every program that uses this key loses access at once.",
    "final": "This cannot be undone; create a new key if you need one again.",
    "action": "Revoke",
    "done": "The key «{{name}}» was revoked."
  },
  "templates": {
    "read_only": {
      "name": "Read-only",
      "lead": "Looks at everything in its projects, changes nothing."
    },
    "monitoring": {
      "name": "Monitoring",
      "lead": "Lists, details, metrics and operations: enough for dashboards and alerts."
    },
    "operator": {
      "name": "Operator",
      "lead": "Reads, powers servers on and off, reboots; the console only if secret actions are allowed."
    },
    "developer": {
      "name": "Developer",
      "lead": "Everything except deleting, rebuilding and removing backups."
    },
    "full_access": {
      "name": "Full access",
      "lead": "Everything its owner may do; deleting waits for a person's approval in “Approvals”."
    },
    "ci_deploy": {
      "name": "CI deploy",
      "lead": "Reads, creates and deletes servers, powers them, applies firewalls, attaches networks."
    }
  },
  "groups": {
    "projects": "Projects",
    "members": "Project access",
    "catalogue": "Catalogue",
    "operations": "Operations",
    "activities": "Activity feed",
    "dashboard": "Dashboard",
    "servers": "Servers",
    "images": "Snapshots and backups",
    "ssh_keys": "SSH keys",
    "networks": "Networks",
    "firewalls": "Firewalls",
    "public_ips": "Public IPs",
    "floating_ips": "Floating IPs",
    "volumes": "Volumes",
    "placement_groups": "Placement groups",
    "api_keys": "API keys",
    "api_policies": "Policies",
    "api_settings": "API settings",
    "service_accounts": "Service accounts",
    "users": "Users",
    "invitations": "Invitations",
    "auth": "Sign-in",
    "me": "Your account",
    "events": "Live updates",
    "meta": "Service",
    "service": "Service",
    "stand": "Test stand",
    "account": "Your account",
    "connections": "Provider connection",
    "load_balancers": "Load balancers",
    "certificates": "Certificates",
    "costs": "Costs",
    "budgets": "Budgets",
    "notifications": "Notifications",
    "alerts": "Alerts",
    "autopilot": "Monitoring (Autopilot)",
    "packages": "Packages",
    "assistant": "AI assistant",
    "mcp": "MCP server",
    "approvals": "Approvals",
    "oauth": "OAuth",
    "connected_apps": "Connected apps"
  },
  "risk": {
    "read": "Read",
    "operate": "Operate",
    "change": "Change",
    "create": "Create",
    "destroy": "Delete",
    "secret": "Secrets",
    "admin": "Administration"
  },
  "matrix": {
    "label": "What the key may do, by resource and kind of action",
    "group": "Resource",
    "column": "All resources: {{risk}}",
    "cell": "{{group}}: {{risk}}",
    "states": {
      "all": "Allowed",
      "some": "Partly allowed",
      "approval": "Needs approval",
      "none": "Not allowed",
      "absent": "No such actions"
    },
    "view": "View",
    "views": {
      "matrix": "Matrix",
      "yaml": "YAML"
    },
    "extraAllow": "Also allow",
    "extraAllowHint": "Single actions or patterns, one per line: servers.reboot, volumes.*, risk:read.",
    "deny": "Always deny",
    "denyHint": "Wins over everything else, e.g. servers.delete or risk:destroy."
  },
  "wizard": {
    "title": "New API key",
    "intro": "Five short steps. The key is shown once at the end; store it in your password manager or CI secrets.",
    "steps": "Steps",
    "step": {
      "who": "Who",
      "policy": "What it may do",
      "projects": "Where",
      "limits": "Limits",
      "review": "Check and create"
    },
    "name": "Name",
    "nameHint": "Say what uses it, e.g. «GitHub Actions deploy».",
    "description": "Description",
    "owner": "Belongs to",
    "ownerMe": "Me",
    "ownerMeLead": "The key never gets more rights than you have, and stops when you leave the organisation.",
    "ownerAccount": "Service account ({{role}}): its own rights, independent of any person",
    "policy": "Policy",
    "allowSecret": "Allow secret actions",
    "allowSecretLead": "Root passwords, the console, SSH keys. Off unless this key really needs them.",
    "allowSecretOff": "Your organisation does not grant secret actions to keys (Endpoints tab).",
    "projects": "Projects",
    "allProjectsLead": "Every project its owner reaches, also ones added later.",
    "someProjects": "Chosen projects",
    "someProjectsLead": "Only the projects ticked below.",
    "lifetime": "Valid for",
    "days": "Days: {{count}}",
    "ipAllowList": "Allowed addresses",
    "ipAllowListHint": "IP addresses or ranges, one per line (203.0.113.7, 10.0.0.0/8). Empty: from anywhere.",
    "rateLimit": "Requests per minute",
    "rateLimitHint": "Empty: the organisation's default ({{count}} per minute).",
    "anywhere": "From anywhere",
    "rateDefault": "Organisation default ({{count}} per minute)",
    "perMinute": "{{count}} per minute",
    "back": "Back",
    "next": "Next",
    "create": "Create key",
    "createdTitle": "Key «{{name}}» created",
    "shownOnce": "Copy the key now: it is shown only this once. We keep only a fingerprint of it.",
    "token": "API key",
    "toKey": "Open the key",
    "done": "Done"
  },
  "rotate": {
    "title": "Rotate the key",
    "lead": "A new secret replaces the old one. Programs keep working with the old secret for the time you choose, so you can update them.",
    "grace": "Old secret keeps working",
    "graces": {
      "0": "Not at all (stops now)",
      "1": "For 1 hour",
      "24": "For 24 hours",
      "72": "For 3 days",
      "168": "For 7 days"
    },
    "action": "Rotate",
    "doneTitle": "New secret"
  },
  "key": {
    "saved": "Key saved.",
    "rateHint": "Empty: the organisation's default.",
    "previousValid": "The secret before the last rotation still works until {{when}}.",
    "createdBy": "Created by",
    "lastUsedFrom": "{{when}} from {{ip}} ({{client}})",
    "requests": "Requests",
    "revokedAt": "Revoked",
    "whatItMayDo": "What it may do",
    "whatItMayDoLead": "In its projects, and never more than its owner may do. Secret actions only when allowed above.",
    "settings": "Name and limits"
  },
  "activity": {
    "actions": {
      "api_key_created": "Key created",
      "api_key_updated": "Key changed",
      "api_key_rotated": "Key rotated",
      "api_key_revoked": "Key revoked",
      "api_key_refused": "Key refused",
      "access_denied": "Call refused",
      "mcp_tool_call": "MCP tool call",
      "approval_requested": "Approval requested",
      "approval_approved": "Approved",
      "approval_rejected": "Rejected",
      "approval_executed": "Run on approval",
      "approval_failed": "Not run on approval",
      "approval_expired": "Approval expired"
    },
    "empty": "Nothing yet",
    "emptyLead": "Calls of keys that change something, refusals and changes to keys show up here.",
    "at": "When",
    "key": "Key",
    "action": "What",
    "byPerson": "by {{name}}",
    "outcome": "Result",
    "outcomes": {
      "success": "Done",
      "denied": "Refused",
      "failed": "Failed"
    },
    "ip": "Address",
    "filterKey": "Key",
    "allKeys": "All keys",
    "filterAccount": "Service account",
    "allAccounts": "All service accounts",
    "tool": "Tool: {{name}}"
  },
  "accounts": {
    "intro": "Service accounts are members that are programs, not people: a CI pipeline, an AI agent. They have a role and project access like a person, and their keys outlive whoever created them.",
    "create": "Add service account",
    "createTitle": "New service account",
    "editTitle": "Service account",
    "lead": "A person answers for it; without an active owner its keys stop working.",
    "name": "Name",
    "description": "Description",
    "owner": "Answers for it",
    "ownerHint": "The person who looks after this account.",
    "role": "Role",
    "noOwner": "No owner",
    "keys": "Keys",
    "disabled": "Disabled",
    "enabled": "Active",
    "edit": "Change",
    "enable": "Enable",
    "disable": "Disable",
    "enabledDone": "«{{name}}» is active again.",
    "disabledDone": "«{{name}}» is disabled; its keys are refused.",
    "remove": "Remove",
    "removeTitle": "Remove «{{name}}»?",
    "removeKeys": "Its keys ({{count}}) are removed with it and stop working at once.",
    "removed": "«{{name}}» was removed.",
    "empty": "No service accounts",
    "emptyLead": "Add one for a pipeline or an agent whose keys should not depend on a person."
  },
  "policies": {
    "library": "Your policies",
    "libraryLead": "Reusable rules for keys. Changing a policy changes every key that uses it at once.",
    "create": "New policy",
    "newTitle": "New policy",
    "editorIntro": "Tick what keys with this policy may do in their projects. They never get more than their owner may do.",
    "empty": "No policies of your own",
    "emptyLead": "The templates below cover most needs; make a policy of your own when several keys need the same special rules.",
    "name": "Name",
    "description": "Description",
    "usedBy": "Keys",
    "usedByCount": "Keys using it: {{count}}",
    "updated": "Changed",
    "edit": "Change",
    "remove": "Remove",
    "removeTitle": "Remove the policy «{{name}}»?",
    "removeLead": "Only a policy no key uses can be removed.",
    "removed": "The policy «{{name}}» was removed.",
    "templates": "Templates",
    "templatesLead": "Built in; pick one when creating a key, or start a policy of your own from one.",
    "rules": "Rules",
    "rulesLead": "Rows are resources, columns kinds of actions.",
    "startFrom": "Start from a template",
    "untitled": "New policy",
    "notMatrix": "This policy has rules the matrix cannot show exactly (approvals, project lists). Rebuild it as a matrix to edit it here.",
    "rebuild": "Rebuild as matrix"
  },
  "endpoints": {
    "rest": "REST API",
    "restLead": "The same API the panel uses. Send the key as Authorization: Bearer; writes need an Idempotency-Key header.",
    "baseUrl": "Base URL",
    "openapi": "OpenAPI description",
    "mcp": "MCP server for AI agents",
    "mcpLead": "AI agents (Claude Code, Claude Desktop, Cursor, VS Code, Codex and others) connect here with an API key. They see only the tools the key's policy allows.",
    "settings": "Switches and defaults",
    "settingsLead": "For the whole organisation.",
    "apiEnabled": "API is on",
    "apiEnabledLead": "Off: every key is refused at once; the panel keeps working.",
    "allowSecret": "Keys may be given secret actions",
    "allowSecretLead": "Root passwords, the console, SSH keys. Each key still needs it switched on.",
    "defaultRate": "Default requests per minute",
    "defaultRateHint": "For keys without a limit of their own.",
    "maxLifetime": "Longest key life, days",
    "maxLifetimeHint": "Empty: no maximum (administrators may then create keys that never expire).",
    "adminsOnly": "Only administrators change these.",
    "saved": "Settings saved.",
    "mcpUrl": "MCP server URL",
    "mcpOff": "MCP is switched off: AI agents are refused at the MCP server. The REST API has its own switch.",
    "mcpEnabled": "MCP server is on",
    "mcpEnabledLead": "Off: every AI agent is refused at once; the REST API keeps working."
  },
  "docs": {
    "open": "API documentation",
    "title": "API documentation",
    "intro": "Every operation of the API with its permission and kind of action. A key may call an operation when its policy and its owner allow that permission.",
    "example": "Examples",
    "exampleHint": "Replace <project id> with an id from the first answer.",
    "search": "Search by path or permission",
    "nothing": "Nothing matches.",
    "parameters": "Parameters",
    "name": "Name",
    "in": "In",
    "type": "Type",
    "body": "Request body (JSON schema)",
    "responses": "Answers: {{codes}}"
  },
  "mcp": {
    "title": "Connect an AI agent (MCP)",
    "client": "MCP client",
    "snippet": "Connection for {{client}}",
    "placeholder": "Replace csk_… with the key.",
    "safety": "The agent sees only the tools this key's policy allows. Deleting needs the resource's exact name, and passwords are never shown to it.",
    "oauthLater": "claude.ai, the connectors of the Claude apps and ChatGPT sign in through the browser (OAuth); that comes in a next step. Until then Claude Desktop connects through mcp-remote, as shown.",
    "clients": {
      "claude-code": {
        "name": "Claude Code",
        "where": "Run it in a terminal where CLOUDSERVER_API_KEY is set."
      },
      "claude-desktop": {
        "name": "Claude Desktop",
        "where": "Add it to claude_desktop_config.json (Settings → Developer → Edit config) and restart Claude Desktop. Needs Node.js."
      },
      "cursor": {
        "name": "Cursor",
        "where": "Add it to ~/.cursor/mcp.json (or .cursor/mcp.json in a project). Cursor reads CLOUDSERVER_API_KEY from the environment."
      },
      "vscode": {
        "name": "VS Code (Copilot)",
        "where": "Save it as .vscode/mcp.json. VS Code asks for the key once and keeps it in its secret storage."
      },
      "codex": {
        "name": "Codex CLI",
        "where": "Add it to ~/.codex/config.toml. Codex reads CLOUDSERVER_API_KEY from the environment."
      },
      "windsurf": {
        "name": "Windsurf",
        "where": "Add it to ~/.codeium/windsurf/mcp_config.json."
      },
      "gemini": {
        "name": "Gemini CLI",
        "where": "Add it to ~/.gemini/settings.json. Gemini CLI reads CLOUDSERVER_API_KEY from the environment."
      },
      "other": {
        "name": "Another client",
        "where": "Any MCP client that speaks Streamable HTTP and sends its own headers."
      }
    }
  },
  "approvals": {
    "lead": "Calls of keys, connected apps and AI agents that their policy sends to a person. Whoever may do it themselves decides: an administrator or the project's owner.",
    "open": "Waiting",
    "closed": "History",
    "empty": "No requests",
    "emptyLead": "When a key with Full access or its own rules asks, say, to delete a resource, the request appears here and you get a notification.",
    "emptyClosed": "No decided requests yet.",
    "waitingForMe": "Waiting for your decision: {{count}}",
    "action": "Action",
    "target": "On",
    "project": "Project",
    "requester": "Asked by",
    "when": "When",
    "expires": "Expires",
    "expiresIn": "expires {{when}}",
    "status": "Status",
    "openItem": "Open",
    "statuses": {
      "pending": "Waiting",
      "approved": "Running",
      "executed": "Done",
      "failed": "Not done",
      "rejected": "Rejected",
      "expired": "Expired"
    },
    "back": "All requests",
    "title": "Approval request",
    "intro": "A key asks for an action its policy sends to a person. Once approved, exactly this request runs, once, as the key.",
    "what": "What will be done",
    "who": "Who asks",
    "why": "Why it needs approval",
    "request": "Request",
    "method": "Method and path",
    "body": "Parameters",
    "noBody": "No parameters",
    "fingerprint": "Request fingerprint",
    "fingerprintHint": "Approving confirms exactly this fingerprint: if the request is changed, it does not run.",
    "key": "Key",
    "owner": "Key owner",
    "serviceAccount": "service account",
    "answersFor": "Answers for the key",
    "client": "Program",
    "ip": "Address",
    "via": "Channel",
    "viaRest": "REST API",
    "viaMcp": "MCP, tool {{tool}}",
    "policy": "Policy",
    "risk": "Risk class",
    "createdAt": "Asked",
    "expiresAt": "Valid until",
    "cost": "Cost",
    "costMonthly": "{{amount}} per month",
    "costToMonthEnd": "until the month ends: {{amount}}",
    "costForecast": "{{from}} → {{to}}",
    "costNone": "This action adds no cost.",
    "costUnknown": "The cost cannot be worked out now (the resource or its price is not in the catalogue).",
    "budgetRuleNew": "Budget rule: a new paid service",
    "budgetRuleChange": "Budget rule: a change to an existing service",
    "budgetBlocked": "The project's budget does not allow this for the key's owner: it will be refused when it runs.",
    "budgetOk": "Within the project's budget.",
    "budgetCheckedAgain": "The budget is checked again when it runs.",
    "decision": "Decision",
    "decidedBy": "Decided by {{name}}, {{when}}",
    "note": "Comment",
    "result": "Result",
    "resultStatus": "HTTP answer {{status}}",
    "approve": "Approve and run",
    "approveNote": "Comment (optional)",
    "approveLead": "The request runs at once, once, as the key “{{key}}”. Your second factor is needed.",
    "reject": "Reject",
    "rejectTitle": "Reject the request",
    "rejectReason": "Reason",
    "rejectReasonHint": "The key's owner and the program that asked will see it.",
    "approved": "Approved and run",
    "approvedFailed": "Approved, but not done",
    "rejected": "Request rejected",
    "cannotDecide": "An administrator or the project's owner decides this request.",
    "destroyWarning": "This action destroys data or resources; it cannot be undone.",
    "forecast": "Month's forecast",
    "app": "Connected app"
  }
}
